import aa from 'search-insights'

// Fields a product card needs (prices, stock, first image, link).
export const PRODUCT_CARD_FIELDS = [
	'id',
	'bcId',
	'sku',
	'name',
	'customUrl',
	'images',
	'currentPrice',
	'price',
	'wasPrice',
	'isPriceHidden',
	'priceHiddenLabel',
	'outOfStock',
	'inventoryTracking',
	'purchaseIncrement',
	'variants',
	'rating',
	'brand',
	'collection',
]

const PLACEHOLDER_IMAGE = '/images/product-placeholder.svg'

// Same rules as the website's product card (front/src/components/products/product.vue).
export function productUrl(product, { queryID, position } = {}) {
	const slug = product?.customUrl?.url?.replace('/', '')?.trim() || product?.id
	const base = `/${product?.collection || 'products'}/${slug}`
	if (!queryID) return base
	const query = new URLSearchParams({ queryID, searchResult: 'true', ...(position != null ? { position: String(position) } : {}) })
	return `${base}?${query}`
}

export function productThumb(product) {
	return (
		product?.image?.url ||
		product?.images?.find((i) => i?.isThumbnail)?.thumbnail ||
		product?.images?.find((i) => i?.isThumbnail)?.url ||
		product?.images?.[0]?.thumbnail ||
		product?.images?.[0]?.url ||
		PLACEHOLDER_IMAGE
	)
}

export function isPriceHidden(product) {
	return !!(product?.isPriceHidden || product?.priceHiddenLabel)
}

export function displayPrice(product) {
	const variantPrices = (product?.variants || [])
		.map((v) => v?.currentPrice ?? v?.calculatedPrice ?? v?.salePrice ?? v?.price)
		.filter((p) => typeof p === 'number' && !Number.isNaN(p))
	if (variantPrices.length) return Math.min(...variantPrices)
	const price = product?.currentPrice
	return typeof price === 'number' && !Number.isNaN(price) ? price : null
}

export function hasVariantPriceRange(product) {
	const prices = new Set((product?.variants || []).map((v) => v?.currentPrice ?? v?.calculatedPrice ?? v?.price).filter((p) => typeof p === 'number'))
	return prices.size > 1
}

export function wasPrice(product) {
	return product?.wasPrice && product.wasPrice > product.currentPrice ? product.wasPrice : null
}

export function variantsOutOfStock(product) {
	return product?.variants?.length && product.inventoryTracking === 'variant' && product.variants.every((v) => v.inventoryLevel <= 0)
}

export function showsAsOutOfStock(product) {
	if (isPriceHidden(product)) return false
	return !!(product?.outOfStock || variantsOutOfStock(product))
}

export function purchaseIncrement(product, variant) {
	const variantInc = Number((variant || product?.variants?.[0])?.purchaseIncrement)
	if (Number.isFinite(variantInc) && variantInc > 0) return variantInc
	const productInc = Number(product?.purchaseIncrement)
	if (Number.isFinite(productInc) && productInc > 0) return productInc
	return 1
}

// Products that can't be added straight from a card (options to pick, birth kits, call for price).
export function needsProductPage(product) {
	return product?.collection === 'birthKits' || (product?.variants?.length || 0) > 1 || isPriceHidden(product)
}

/*
	Add to the bag the way the website does: the cart helper (front/src/helpers/cart.js)
	watches store.cart.cartItems and saves the change to the member's / guest cart.
*/
export function addToCart(store, product, { quantity, variant, optionSelections, queryID } = {}) {
	const qty = quantity || purchaseIncrement(product, variant)
	const chosenVariant = variant || product?.variants?.[0]
	const sameLine = (line) =>
		line.item.id === product.id &&
		(line.variant?.id || null) === (chosenVariant?.id || null) &&
		JSON.stringify(line.optionSelections || null) === JSON.stringify(optionSelections || null)

	const existing = store.cart?.cartItems?.find?.(sameLine)
	if (existing) {
		existing.quantity += qty
	} else {
		store.cart?.cartItems?.push?.({
			item: product,
			quantity: qty,
			variant: chosenVariant,
			...(optionSelections ? { optionSelections } : {}),
			...(queryID ? { queryID } : {}),
		})
	}

	const price = chosenVariant?.currentPrice ?? product?.currentPrice
	if (typeof window.gtag === 'function') {
		window.gtag('event', 'add_to_cart', {
			send_to: 'AW-387509916',
			ecomm_pagetype: 'cart',
			currency: 'USD',
			items: [{ id: product?.sku, name: product?.name, quantity: qty, google_business_vertical: 'retail', currency: 'USD', price }],
			value: price,
		})
	}
	try {
		if (queryID) {
			aa('addedToCartObjectIDsAfterSearch', {
				eventName: 'Product Added To Cart',
				index: 'InHisHands',
				objectIDs: [product.id],
				objectData: [{ queryID, price, quantity: qty }],
				currency: 'USD',
			})
		} else {
			aa('addedToCartObjectIDs', {
				eventName: 'Product Added To Cart',
				index: 'InHisHands',
				objectIDs: [product.id],
				objectData: [{ price, quantity: qty }],
				currency: 'USD',
				value: price,
			})
		}
	} catch {
		// analytics must never block the bag
	}
	return qty
}
